import { PermissionEnum, UserPermissions } from '@creditsights/api-ui-web';
import { Button, ButtonMode, ButtonSize, Checkbox, CoverageIndicator, Filter, Icon, ICONS, Loader } from '@creditsights/styleguide-web';
import { debounce, remove } from 'lodash';
import numeral from 'numeral';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import CountryFlag from '../../components/country-flag/CountryFlag';
import AddToWatchlist from '../../components/watchlist/AddToWatchlist';
import { COVERAGE_KEYS, CS_VIEW_DATA, NO_DATA, TAG_TYPES, WEIGHTING_ICONS_MAP } from '../../Constants';
import useFetchCompanies from '../../hooks/company/useFetchCompanies';
import useFetchFilters from '../../hooks/filter/useFetchFilters';
import useFetchUserPermissions from '../../hooks/user/useFetchUserPermissions';
import { formatDate } from '../../utils/date.utils';
import { getOptionsWithTwoLevels } from '../../utils/filter.utils';
import { hasMoreData } from '../../utils/page.utils';
import { parseTag } from '../../utils/tag.utils';
import './Companies.scss';

const canAccessResearch = (userPermissions: UserPermissions) => userPermissions?.permissions.includes(PermissionEnum.RESEARCH);

function Companies(): ReactElement {
  const { data: filters, isLoading: filtersLoading } = useFetchFilters();
  const { isLoading: permissionsLoading, data: userPermissions } = useFetchUserPermissions();
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const [filterConfig, setFilterConfig] = useState<any>({});
  const [searchText, setSearchText] = useState('');
  const [rawSearchText, setRawSearchText] = useState('');
  const [coverage, setCoverage] = useState<string[]>([]);
  const [csView, setCsView] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [sort, setSort] = useState({ field: 'name', asc: true });
  const { data, isLoading, fetchNextPage, isFetchingNextPage } = useFetchCompanies(searchText, coverage, csView, sort, filterConfig);

  const debouncedSetSearchText = useMemo(() => debounce(setSearchText, 500), []);

  const sectorFilter = useMemo<any>(() => getOptionsWithTwoLevels(filters?.config.sector || []), filters);

  useEffect(() => {
    if (search) {
      try {
        const config = JSON.parse(decodeURI(search.substring(1)));
        setSearchText(config.searchText);
        setCoverage(config.coverage);
        setCsView(config.csView);
        setSort(config.sort);
        setFilterConfig(config.filterConfig);
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  useEffect(() => {
    const defaultCoverageSelection: string[] = [];

    if (userPermissions?.permissions.includes(PermissionEnum.RESEARCH)) {
      defaultCoverageSelection.push(COVERAGE_KEYS.cs);
    }
    if (userPermissions?.permissions.includes(PermissionEnum.COVENANTS)) {
      defaultCoverageSelection.push(COVERAGE_KEYS.cr);
    }
    if (userPermissions?.permissions.includes(PermissionEnum.NEWS)) {
      defaultCoverageSelection.push(COVERAGE_KEYS.lfi);
    }
    if (userPermissions?.permissions.includes(PermissionEnum.FINANCIALS)) {
      defaultCoverageSelection.push(COVERAGE_KEYS.financials);
    }

    setCoverage(defaultCoverageSelection);
  }, [userPermissions]);

  useEffect(() => {
    debouncedSetSearchText(rawSearchText);
  }, [rawSearchText]);

  useEffect(() => {
    setSelected([]);
    navigate(`${pathname}?${JSON.stringify({ searchText, coverage, csView, sort, filterConfig })}`, { replace: true });
  }, [searchText, coverage, csView, sort, filterConfig]);

  const renderTag = (tags, type) => {
    const nodes = tags
      .filter((tag) => tag.startsWith(type))
      .filter((tag) => filters.map[tag])
      .map((tag) => (
        <div className="tag" key={tag}>
          {filters.map[tag]}
        </div>
      ));

    return nodes.length > 0 ? nodes : NO_DATA;
  };

  const renderCountry = (tagIds: string[]) => {
    const countryTagsIds = tagIds.filter((tagId) => tagId.startsWith(TAG_TYPES.country));
    if (!countryTagsIds.length) {
      return NO_DATA;
    }

    return countryTagsIds.map((tagId) => <CountryFlag iso3Code={parseTag(tagId)?.[1]} countryName={filters.map[tagId]} key={tagId} />);
  };

  const renderWeight = (company) => {
    const { csCoverage, latestRec } = company;
    if (!csCoverage) {
      return NO_DATA;
    }
    return <Icon name={WEIGHTING_ICONS_MAP[latestRec?.weighting.toLowerCase()]} height={30} width={30} />;
  };

  const getCompanyPageLink = (company) => {
    const { tagId } = company;
    const [tagType, tagValue] = tagId.split('/');
    return tagType === 'company' ? `/companies/${tagValue}` : `/municipals/${tagValue}`;
  };

  const renderCreditGrade = (tags) => {
    const array: string[] = [];

    for (const tag of tags) {
      // eslint-disable-next-line default-case
      switch (tag) {
        case 'tag/investment_grade':
          array.push('Investment Grade');
          break;
        case 'tag/high_yield':
          array.push('High Yield');
          break;
        case 'tag/special_situations':
          array.push('Special Situations');
          break;
      }
    }
    const nodes = array.map((item) => (
      <div className="tag" key={item}>
        {item}
      </div>
    ));

    return nodes.length > 0 ? nodes : NO_DATA;
  };

  const updateFilterConfig = (key, selected) => {
    setFilterConfig((config) => {
      // eslint-disable-next-line no-param-reassign
      config[key] = selected;

      if (selected.length === 0) {
        // eslint-disable-next-line no-param-reassign
        delete config[key];
      }

      return { ...config };
    });
  };

  const renderCoverage = ({ csCoverage, crCoverage, lfiCoverage, financialsCoverage }) => {
    return <CoverageIndicator csCoverage={csCoverage} lfiCoverage={lfiCoverage} crCoverage={crCoverage} financialsCoverage={financialsCoverage} />;
  };

  const toggleCoverage = (product) => {
    const newCoverage = coverage.includes(product) ? coverage.filter((x) => x !== product) : [...coverage, product];
    setCoverage(newCoverage);
  };

  const updateSort = (field) => {
    setSort((v) => {
      if (v.field === field) {
        return {
          field,
          asc: !v.asc,
        };
      }
      return {
        field,
        asc: true,
      };
    });
  };

  const handleSelection = (checked, tagId) => {
    setSelected((array) => {
      if (checked) {
        array.push(tagId);
      } else {
        remove(array, (item) => item === tagId);
      }

      return [...array];
    });
  };

  const resetFilters = () => {
    setFilterConfig({});
    setCoverage([]);
    setCsView([]);
    setRawSearchText('');
  };

  return (
    <div className="cs-companies-ctx">
      <div className="page-title">Companies</div>
      {!(filtersLoading || permissionsLoading) && (
        <div className="filters-ctx">
          <div className="filter-text">
            <i className="search icon" />
            <input type="text" className="search" placeholder="Issuer/Borrower" value={rawSearchText} onChange={(e) => setRawSearchText(e.currentTarget.value)} />
            {rawSearchText && <i className="times icon cancel" onClick={() => setRawSearchText('')} />}
          </div>
          <Filter data={filters.config.region} placeholder="Region" checkedKeys={filterConfig.region || []} onChange={(checked) => updateFilterConfig('region', checked)} />
          <Filter data={filters.config.country} placeholder="Country" checkedKeys={filterConfig.country || []} onChange={(checked) => updateFilterConfig('country', checked)} />
          <Filter data={sectorFilter} placeholder="Sector/Subsector" checkedKeys={filterConfig.sector || []} onChange={(checked) => updateFilterConfig('sector', checked)} />
          <Filter data={filters.config.creditGrade} placeholder="Credit Grade" checkedKeys={filterConfig.creditGrade || []} onChange={(checked) => updateFilterConfig('creditGrade', checked)} />
          {canAccessResearch(userPermissions!) && <Filter data={CS_VIEW_DATA} placeholder="CS View" checkedKeys={csView} onChange={(checked) => setCsView(checked)} />}
          <Filter data={filters.config.assetClass} placeholder="Asset Class" checkedKeys={filterConfig.assetClass || []} onChange={(checked) => updateFilterConfig('assetClass', checked)} />
          <Filter data={filters.config.currency} placeholder="Currency" checkedKeys={filterConfig.currency || []} onChange={(checked) => updateFilterConfig('currency', checked)} />
          <Checkbox className="research" label="Research" checked={coverage.includes('cs')} onChange={() => toggleCoverage('cs')} dataTestId="cs" />
          <Checkbox className="research" label="Covenants" checked={coverage.includes('cr')} onChange={() => toggleCoverage('cr')} dataTestId="cr" />
          <Checkbox className="news" label="News" checked={coverage.includes('lfi')} onChange={() => toggleCoverage('lfi')} dataTestId="lfi" />
          <Checkbox className="financials" label="Financials" checked={coverage.includes('financials')} onChange={() => toggleCoverage('financials')} dataTestId="financials" />
          <div className="separator" />
          <button className="reset-button" onClick={resetFilters} type="button">
            <Icon name={ICONS.Clear} width={12} height={12} /> Reset
          </button>
        </div>
      )}

      {(isLoading || filtersLoading || permissionsLoading) && <Loader className="cs-loader" position="center" />}

      {!(isLoading || filtersLoading || permissionsLoading) && (
        <>
          {data?.pages[0] && (
            <div className="sub-header">
              <div className="count-txt">{numeral(data?.pages[0]?.totalResults).format('0,0')} results</div>
              <div className="actions-ctx">{selected.length > 0 && <AddToWatchlist companies={selected} />}</div>
            </div>
          )}
          <div className="table-ctx">
            <table className="ui table">
              <thead>
                <tr>
                  <th className="selection" />
                  {canAccessResearch(userPermissions!) && (
                    <th className="cs-view sortable" onClick={() => updateSort('latestRecommendation.weighting')}>
                      CS View <i className="sort icon" />
                    </th>
                  )}
                  <th className="sortable" onClick={() => updateSort('name')}>
                    Issuer/Borrower <i className="sort icon" />{' '}
                  </th>
                  <th className="sortable" onClick={() => updateSort('ticker')}>
                    Ticker <i className="sort icon" />
                  </th>
                  {canAccessResearch(userPermissions!) && (
                    <th className="sortable" onClick={() => updateSort('latestRecommendation.changedDate')}>
                      Rec Changed <i className="sort icon" />
                    </th>
                  )}
                  {canAccessResearch(userPermissions!) && (
                    <th className="sortable" onClick={() => updateSort('latestRecommendation.publishDate')}>
                      Rec Reviewed <i className="sort icon" />
                    </th>
                  )}
                  <th className="sortable" onClick={() => updateSort('sector')}>
                    Sector/Subsector <i className="sort icon" />
                  </th>
                  <th className="sortable" onClick={() => updateSort('creditQualities')}>
                    Credit Grade <i className="sort icon" />
                  </th>
                  <th className="sortable" onClick={() => updateSort('topLevelRegion')}>
                    Region <i className="sort icon" />
                  </th>
                  <th>Country</th>
                  <th>Coverage</th>
                </tr>
              </thead>
              <tbody>
                {data?.pages.map((page, pageIndex) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <React.Fragment key={`page-${pageIndex}`}>
                    {page.results.map((company) => (
                      <tr key={company.tagId}>
                        <td>
                          <Checkbox dataTestId={company.tagId} checked={selected.indexOf(company.tagId) !== -1} onChange={(e, data) => handleSelection(data.checked, company.tagId)} />
                        </td>
                        {canAccessResearch(userPermissions!) && (
                          <td>
                            <div className="weight">{renderWeight(company) || NO_DATA}</div>
                          </td>
                        )}
                        <td>
                          <NavLink to={getCompanyPageLink(company)} key={company.tagId} className="company">
                            {company.displayName}
                          </NavLink>
                        </td>
                        <td>{company.displayTicker || NO_DATA}</td>
                        {canAccessResearch(userPermissions!) && <td>{formatDate(company.latestRec?.changedDate) || NO_DATA}</td>}
                        {canAccessResearch(userPermissions!) && <td>{formatDate(company.latestRec?.publishDate) || NO_DATA}</td>}
                        <td>{renderTag(company.implied, 'sector')}</td>
                        <td>{renderCreditGrade(company.implied)}</td>
                        <td>{renderTag(company.implied, 'region')}</td>
                        <td>{renderCountry(company.implied)}</td>
                        <td>{renderCoverage(company)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {hasMoreData(data, 100, data?.pages[0]?.totalResults) && (
              <div className="next-page">
                <Button mode={ButtonMode.PRIMARY} size={ButtonSize.MEDIUM} loading={isFetchingNextPage} disabled={isFetchingNextPage} onClick={() => fetchNextPage()}>
                  Load 100 more
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default Companies;
